import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function AdminBlogs() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    titleUrdu: '',
    excerpt: '',
    excerptUrdu: '',
    content: '',
    contentUrdu: '',
    image: '',
    author: '',
    authorUrdu: '',
    category: '',
    categoryUrdu: '',
    tags: '',
    publishedDate: new Date().toLocaleDateString('en-CA'),
    order: 0,
    isPublished: true
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin/login");
        return;
      }

      const response = await api.get("/admin/blogs/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data.blogs || []);
    } catch (error) {
      console.error("Fetch blogs error:", error);
      toast.error("Blogs لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const handleContentChange = (value, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      titleUrdu: '',
      excerpt: '',
      excerptUrdu: '',
      content: '',
      contentUrdu: '',
      image: '',
      author: '',
      authorUrdu: '',
      category: '',
      categoryUrdu: '',
      tags: '',
      publishedDate: new Date().toLocaleDateString('en-CA'),
      order: 0,
      isPublished: true
    });
    setEditingBlog(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("adminToken");
      const tagsArray = formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [];

      const payload = {
        title: formData.title,
        titleUrdu: formData.titleUrdu,
        excerpt: formData.excerpt,
        excerptUrdu: formData.excerptUrdu,
        content: formData.content,
        contentUrdu: formData.contentUrdu,
        image: formData.image,
        author: formData.author,
        authorUrdu: formData.authorUrdu,
        category: formData.category,
        categoryUrdu: formData.categoryUrdu,
        tags: tagsArray,
        publishedDate: formData.publishedDate,
        order: formData.order,
        isPublished: formData.isPublished
      };

      if (editingBlog) {
        await api.put(`/admin/blogs/${editingBlog._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog اپڈیٹ ہو گیا");
      } else {
        await api.post("/admin/blogs", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Blog شامل ہو گیا");
      }

      setShowAddModal(false);
      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error("Save blog error:", error);
      toast.error(error.response?.data?.message || "Blog محفوظ کرنے میں ناکام");
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title || '',
      titleUrdu: blog.titleUrdu || '',
      excerpt: blog.excerpt || '',
      excerptUrdu: blog.excerptUrdu || '',
      content: blog.content || '',
      contentUrdu: blog.contentUrdu || '',
      image: blog.image || '',
      author: blog.author || '',
      authorUrdu: blog.authorUrdu || '',
      category: blog.category || '',
      categoryUrdu: blog.categoryUrdu || '',
      tags: blog.tags ? blog.tags.join(', ') : '',
      publishedDate: blog.publishedDate ? new Date(blog.publishedDate).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
      order: blog.order || 0,
      isPublished: blog.isPublished !== undefined ? blog.isPublished : true
    });
    setShowAddModal(true);
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('کیا آپ واقعی یہ blog حذف کرنا چاہتے ہیں؟')) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      await api.delete(`/admin/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Blog حذف ہو گیا");
      fetchBlogs();
    } catch (error) {
      console.error("Delete blog error:", error);
      toast.error("Blog حذف کرنے میں ناکام");
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <>
      <Helmet><title>Blogs Management - Admin Panel</title></Helmet>

      <div className="min-h-screen bg-brand-background">
        {/* Mobile Menu Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Sidebar - Fixed */}
          <aside className={`fixed top-0 left-0 h-screen w-64 bg-white shadow-md border-r border-brand-card/60 z-40 overflow-y-auto transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
            }`}>
            <div className="p-4 border-b border-brand-card/40 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-brand-header">Admin Navigation</h2>
                <p className="text-xs text-gray-500 mt-1">Quick access to tools</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="p-3 space-y-2 text-sm">
              <button
                onClick={() => {
                  navigate('/admin');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/bills');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Bill Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Field Settings</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/roznamacha-fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Roz Namacha Fields</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recoveries');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recovery Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/expenses');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Expense Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/payment-methods');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Payment Methods</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/team-members');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Team Members</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recognitions');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recognitions</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/blogs');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Blogs</span>
              </button>
              <button
                onClick={() => {
                  navigate('/company/dashboard');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Company Dashboard</span>
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem("adminToken");
                  localStorage.removeItem("adminData");
                  navigate("/admin/login");
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Logout</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 md:ml-64 p-4 md:p-8">
            {/* Mobile Header with Menu Button */}
            <div className="md:hidden mb-4 flex items-center justify-between bg-white rounded-lg shadow-md p-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-gray-900">Blogs</h1>
              <div className="w-6"></div>
            </div>

            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 mb-6 border border-brand-card/60">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-brand-header">Blogs Management</h1>
                  <p className="text-gray-600 mt-1 text-sm md:text-base">Manage blog posts for the website</p>
                </div>
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 md:px-6 py-2 md:py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg text-sm md:text-base"
                >
                  + Add Blog
                </button>
              </div>
            </div>

            {/* Blogs List */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-brand-card/60">
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading blogs...</p>
                </div>
              ) : blogs.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-600">No blogs found. Add your first blog post!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-brand-background">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Image
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Author
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Published Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {blogs.map((blog) => (
                        <tr key={blog._id} className="hover:bg-brand-background/60 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {blog.image ? (
                              <img src={blog.image} alt={blog.title} className="h-16 w-24 object-cover rounded" />
                            ) : (
                              <div className="h-16 w-24 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                                No Image
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{blog.title}</div>
                            {blog.titleUrdu && (
                              <div className="text-sm text-gray-500">{blog.titleUrdu}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{blog.author || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{blog.category || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {blog.publishedDate ? new Date(blog.publishedDate).toLocaleDateString() : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{blog.views || 0}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${blog.isPublished
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-red-50 text-brand-alert border border-red-200"
                              }`}>
                              {blog.isPublished ? "Published" : "Draft"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleEdit(blog)}
                              className="text-indigo-600 hover:text-indigo-900 mr-4"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(blog._id)}
                              className="text-brand-alert hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingBlog ? 'Edit Blog' : 'Add New Blog'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title (English) *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Blog Title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title (Urdu)
                    </label>
                    <input
                      type="text"
                      name="titleUrdu"
                      value={formData.titleUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="بلاگ عنوان"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt (English)
                    </label>
                    <textarea
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      rows="2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Short description..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Excerpt (Urdu)
                    </label>
                    <textarea
                      name="excerptUrdu"
                      value={formData.excerptUrdu}
                      onChange={handleInputChange}
                      rows="2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="مختصر تفصیل..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content (English) *
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(value) => handleContentChange(value, 'content')}
                    modules={quillModules}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content (Urdu)
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={formData.contentUrdu}
                    onChange={(value) => handleContentChange(value, 'contentUrdu')}
                    modules={quillModules}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author (English)
                    </label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Author Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author (Urdu)
                    </label>
                    <input
                      type="text"
                      name="authorUrdu"
                      value={formData.authorUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="مصنف کا نام"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category (English)
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Business"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category (Urdu)
                    </label>
                    <input
                      type="text"
                      name="categoryUrdu"
                      value={formData.categoryUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="کاروبار"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="business, technology, innovation"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Published Date *
                    </label>
                    <input
                      type="date"
                      name="publishedDate"
                      value={formData.publishedDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex items-center pt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isPublished"
                      checked={formData.isPublished}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Published</span>
                  </label>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition"
                  >
                    {editingBlog ? 'Update' : 'Add'} Blog
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminBlogs;

